import {
  Alert,
  Button,
  Divider,
  Group,
  Modal,
  Paper,
  Stack,
  Text,
} from '@mantine/core';
import { IconAlertCircle, IconArrowNarrowRight } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { OrderDisplayCard, OrderDisplayCardProps } from './OrderDisplayCard';
import { OrderForm, OrderFormData } from './OrderForm';
import { useOrderFormWithSave } from '../hooks/useOrderForm';
import { useOrderSupportingDocuments } from '../hooks/useOrderSupportingDocuments';

interface OrderBoxProps extends OrderDisplayCardProps {
  initialFormData?: Partial<OrderFormData>;
  onOrderUpdated?: () => Promise<void> | void;
}

export const OrderBox = ({
  patient,
  requester,
  formData,
  serviceRequest,
  initialFormData,
  onOrderUpdated,
}: OrderBoxProps) => {
  const { t } = useTranslation(['lims', 'common']);
  const [editOpened, { open: openEdit, close: closeEdit }] =
    useDisclosure(false);
  const [documentsToDelete, setDocumentsToDelete] = useState<string[]>([]);

  const {
    orderDocuments: linkedOrderDocuments,
    authDocuments: linkedAuthDocuments,
  } = useOrderSupportingDocuments(serviceRequest ?? null);

  const seedFormData = useMemo(
    () => initialFormData ?? formData,
    [initialFormData, formData]
  );
  const {
    formData: editFormData,
    errors,
    updateField,
    setFormData,
    saving,
    handleSave,
  } = useOrderFormWithSave(
    {
      serviceRequest: serviceRequest ?? null,
      onSuccess: onOrderUpdated,
      documentsToDelete,
    },
    seedFormData
  );

  useEffect(() => {
    if (seedFormData) {
      setFormData((prev) => ({ ...prev, ...seedFormData }));
    }
  }, [seedFormData, setFormData]);

  const handleSaveClick = async () => {
    const success = await handleSave();
    if (success) {
      setDocumentsToDelete([]);
      closeEdit();
    }
  };

  const handleCloseEdit = () => {
    closeEdit();
    setDocumentsToDelete([]);
    if (seedFormData) {
      setFormData((prev) => ({ ...prev, ...seedFormData }));
    }
  };

  return (
    <>
      <Paper shadow="sm" p="md" withBorder flex="1" pos="relative">
        <Stack gap="sm">
          {serviceRequest?.status === 'completed' && (
            <Alert icon={<IconAlertCircle size={16} />} color="blue">
              {t('orderDetails.completedMessage', { ns: 'lims' })}
            </Alert>
          )}

          <Group justify="space-between">
            <Text size="sm" fw={600}>
              {t('orderDetails.orderInfo', { ns: 'lims' })}
            </Text>
            <Button
              variant="white"
              rightSection={<IconArrowNarrowRight />}
              onClick={openEdit}
              disabled={serviceRequest?.status === 'completed'}
            >
              {t('edit', { ns: 'common' })}
            </Button>
          </Group>

          <Divider />

          <OrderDisplayCard
            patient={patient}
            requester={requester}
            formData={formData}
            serviceRequest={serviceRequest}
          />
        </Stack>
      </Paper>

      <Modal
        opened={editOpened}
        onClose={handleCloseEdit}
        title={t('orderDetails.edit', { ns: 'lims' })}
        size="lg"
        centered
      >
        <Stack gap="md">
          <OrderForm
            formData={editFormData}
            errors={errors}
            onFieldChange={updateField}
            linkedOrderDocuments={linkedOrderDocuments}
            linkedAuthDocuments={linkedAuthDocuments}
            documentsToDelete={documentsToDelete}
            onDocumentsToDeleteChange={setDocumentsToDelete}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={handleCloseEdit}>
              {t('orderDetails.cancel', { ns: 'lims' })}
            </Button>
            <Button onClick={handleSaveClick} loading={saving}>
              {t('orderDetails.saveChanges', { ns: 'lims' })}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
